import {touchPointer} from './map-pointer.mjs';
// Touch arbitration only. Mouse clicks and mouse double-click zoom stay native.
export const DOUBLE_TAP_MS = 200, DOUBLE_TAP_DISTANCE = 30;
const MOVE_TOLERANCE = 3, MAX_TOUCH_MS = 500;
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
export function touchTaps({single, double, timers = globalThis, now = () => performance.now()}) {
  let pending = null, active = null, timer;
  function clearTimer() { timers.clearTimeout(timer); timer = undefined; }
  function cancel() { clearTimer(); pending = active = null; }
  function start(points) {
    if (points.length !== 1 || active) { cancel(); return; }
    clearTimer();
    const point = points[0];
    const second = pending && now() - pending.time <= DOUBLE_TAP_MS && distance(point, pending.point) <= DOUBLE_TAP_DISTANCE;
    if (!second) pending = null;
    active = {point: {...point}, time: now(), second};
  }
  function move(points) {
    if (!active || points.length !== 1 || points[0].id !== active.point.id || distance(points[0], active.point) > MOVE_TOLERANCE) cancel();
  }
  function end(points, originalEvent) {
    if (!active) return;
    const tap = active; active = null;
    if (points.length !== 1 || points[0].id !== tap.point.id || distance(points[0], tap.point) > MOVE_TOLERANCE || now() - tap.time > MAX_TOUCH_MS) { cancel(); return; }
    const point = tap.point;
    if (tap.second && pending && now() - pending.time <= DOUBLE_TAP_MS) {
      cancel(); double(point, originalEvent); return;
    }
    pending = {point, time: now()};
    timer = timers.setTimeout(() => {
      const target = pending; pending = null; timer = undefined;
      if (target) single(target.point);
    }, DOUBLE_TAP_MS);
  }
  return {start, move, end, cancel};
}
export function installTouchTaps(map, single, {backdrop, backdropActive = () => false} = {}) {
  const canvas = map.getCanvas();
  // Run after MapLibre finishes touchend; its active handlers stop easings.
  const taps = touchTaps({single, double: (point, originalEvent) => queueMicrotask(() => {
    if (!map.doubleClickZoom.isEnabled()) return;
    const snap = map.getZoomSnap();
    const zoom = map.getZoom() + 1;
    map.easeTo({zoom: snap ? Math.round(zoom / snap) * snap : zoom, duration: 300,
      around: map.unproject(point)}, {originalEvent});
  })});
  const points = touches => {
    const box = canvas.getBoundingClientRect();
    return Array.from(touches, t => ({x: (t.clientX - box.left) / (box.width / canvas.clientWidth),
      y: (t.clientY - box.top) / (box.height / canvas.clientHeight), id: t.identifier}));
  };
  // Mobile detail sheets cover the map with a backdrop. Arbitrate its native
  // touches too, while leaving mouse clicks and other sheet backdrops alone.
  if (backdrop) {
    const listen = (type, handler) => {
      const guarded = event => { if (backdropActive()) handler(event); };
      backdrop.addEventListener(type, guarded, true);
      map.on('remove', () => backdrop.removeEventListener(type, guarded, true));
    };
    listen('touchstart', event => taps.start(points(event.touches)));
    listen('touchmove', event => taps.move(points(event.touches)));
    listen('touchend', event => event.touches.length ? taps.cancel() : taps.end(points(event.changedTouches), event));
    listen('touchcancel', taps.cancel);
    listen('click', event => { if (touchPointer(event, canvas)) { event.preventDefault(); event.stopImmediatePropagation(); } });
  }
  const container = map.getCanvasContainer();
  const suppressMouse = event => {
    if (touchPointer(event, canvas)) { event.preventDefault(); event.stopImmediatePropagation(); }
  };
  // Compatibility mouse events must not stop the touch zoom animation.
  for (const type of ['mousedown', 'mouseup', 'dblclick']) {
    container.addEventListener(type, suppressMouse, true);
    map.on('remove', () => container.removeEventListener(type, suppressMouse, true));
  }
  map.on('touchstart', event => {
    // Pinned MapLibre 6.16: reset only its one-finger recognizer before it sees
    // this start. Keep native mouse double-click, two-finger tap and pinch/pan.
    map.doubleClickZoom._tapZoom._zoomIn.reset();
    taps.start(points(event.originalEvent.touches));
  });
  map.on('touchmove', event => taps.move(points(event.originalEvent.touches)));
  map.on('touchend', event => {
    if (event.originalEvent.touches.length) taps.cancel();
    else taps.end(points(event.originalEvent.changedTouches), event.originalEvent);
  });
  for (const type of ['touchcancel', 'movestart', 'style.load', 'remove']) map.on(type, taps.cancel);
  return taps;
}
